// components/LockScreen.jsx — Anamoria SPA
// v1.2 — NEW-21: right number of PIN slots, absolute attempt cap, dead
//        timed-lockout path removed, approved copy applied (Aug 21, 2026)
//
// Plan:  new21-pin-lockscreen-spa-plan-DRAFT-for-review-v0.3.md
// Copy:  Anamoria_PIN_Lock_Screen_Copy_v0.5.md (closed, applied verbatim)
//
// Changes from v1.1:
//   - NEW prop: pinLength — number | null. The member's actual PIN length,
//     from GET /security/settings (anamoria-security v1.1 + migration 040).
//     Falls back to PIN_MAX_LENGTH when null: older PINs predate the column
//     and are not backfillable, since bcrypt hashes are fixed-length.
//   - NEW prop: onPinLocked — () => void. Called when the server reports the
//     PIN locked. Signals App.jsx to switch lockReason to 'pin_locked'.
//   - Dots row renders `slots`, not a fixed 6. Typing caps at `slots`.
//   - Auto-submit on the final digit, ONLY when pinLength is known. With an
//     unknown length we are guessing at 6, so a 4-digit PIN would never reach
//     it; the explicit Unlock button remains the way through in both cases.
//   - NEW mode 'pin_locked' — its own screen. Deliberately NOT the
//     session_expired screen with different text: two meanings behind one
//     flag is how a future change reintroduces the wrong message.
//   - REMOVED the timed-lockout path (lockedOut, lockedOutSeconds,
//     formatTime, the TOO_MANY_ATTEMPTS branch and its message). The server
//     no longer sends TOO_MANY_ATTEMPTS; the cap is absolute and only setting
//     a new PIN clears it.
//   - Copy v0.5 applied: wrong-PIN states, the locked screen, the
//     session-expired body and button, the network failure line, "Checking…".
//   - Accessibility: the dots are decorative and stay aria-hidden; a polite
//     live region now announces the running count, which nothing did before.
//
// v1.1 — Detect 401/403 during PIN verify → session expired transition (May 20, 2026)
//
// Changes from v1.0:
//   - NEW prop: onSessionExpired — () => void. Called when PIN verification
//     returns 401/403, indicating JWT expired between lock display and PIN
//     entry. Signals App.jsx to switch lockReason to 'session_expired'.
//   - In handleSubmit catch: added 401/403 detection before the generic
//     "Could not verify PIN" fallback. Shows "Your session has expired."
//     for 1.5s, then calls onSessionExpired(). One-way transition per
//     OWASP MSTG: server 401/403 is the authoritative session validity signal.
//   - No other logic or UI changes.
//
// v1.0 — Lock screen overlay: PIN unlock + session expired (May 18, 2026)
//
// Full-viewport overlay rendered at App.jsx level (above all routes).
// Solid warm beige background — content fully hidden (not blurred).
// Anamoria butterfly logo + wordmark matches JoinPage branding.
//
// Three modes:
//   'pin'             — Custom numeric keypad for PIN entry
//   'pin_locked'      — Too many wrong tries; sign in again and choose a new PIN
//   'session_expired' — "Session expired" message with Sign In button
//
// Props:
//   lockReason       — 'pin' | 'pin_locked' | 'session_expired'
//   pinLength        — number | null (the member's actual PIN length)
//   onUnlock         — () => void (called after successful PIN verification)
//   onSessionExpired — () => void (called when PIN verify gets 401/403 — JWT expired)
//   onPinLocked      — () => void (called when the PIN is locked and a new one is needed)
//   getApi           — () => apiClient (for PIN verification call)
//
// "Forgot PIN?" triggers Auth0 logout + re-login (same pattern as
// SettingsPage handleSignOut).

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import styles from './LockScreen.module.css';

const PIN_MIN_LENGTH = 4;
const PIN_MAX_LENGTH = 6;

// v1.2: how long the "that didn't match" beat is held before the locked
// screen replaces it. Matches the existing session-expired transition below,
// so the timing and the pattern are already familiar on this screen.
const LOCK_TRANSITION_MS = 1500;

export default function LockScreen({
  lockReason,
  pinLength,
  onUnlock,
  onSessionExpired,
  onPinLocked,
  getApi,
}) {
  const { logout } = useAuth0();

  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const containerRef = useRef(null);

  // v1.2: draw the member's actual PIN length. Null means the PIN predates
  // migration 040, so we fall back to the maximum and keep the explicit
  // submit button as the only way through.
  const slots = pinLength || PIN_MAX_LENGTH;

  // Handle digit press
  const handleDigit = useCallback((digit) => {
    if (verifying) return;
    setError(null);
    setPin(prev => {
      if (prev.length >= slots) return prev;
      return prev + digit;
    });
  }, [verifying, slots]);

  // Handle backspace
  const handleBackspace = useCallback(() => {
    if (verifying) return;
    setError(null);
    setPin(prev => prev.slice(0, -1));
  }, [verifying]);

  // Handle PIN submit
  const handleSubmit = useCallback(async () => {
    if (pin.length < PIN_MIN_LENGTH || verifying) return;

    setVerifying(true);
    setError(null);
    try {
      const api = getApi();
      const result = await api.post('/security/pin/verify', { pin });

      if (result.valid) {
        // Unlock the app
        setPin('');
        onUnlock();
      } else {
        // Wrong PIN
        setPin('');
        setAttemptsRemaining(result.attemptsRemaining);
        if (result.attemptsRemaining === 1) {
          setError("That PIN didn't match. One more try before you'll need to sign in again.");
        } else if (result.attemptsRemaining !== undefined) {
          setError(`That PIN didn't match. ${result.attemptsRemaining} tries left before you'll need to sign in again.`);
        } else {
          setError("That PIN didn't match. Please try again.");
        }
      }
    } catch (err) {
      setPin('');
      if (err.error === 'PIN_LOCKED_RESET_REQUIRED') {
        // v1.2: the server returns an IDENTICAL body whether the PIN has just
        // locked (index.mjs:206) or was already locked before this attempt
        // (index.mjs:178), so the response cannot tell us which happened. On
        // the already-locked path it returns before the PIN is compared, so
        // "that PIN didn't match" would state something never checked.
        // Only say it when we watched the count reach one; otherwise go
        // straight to the locked screen, which explains the situation on its
        // own and claims nothing about the PIN just typed.
        if (attemptsRemaining === 1) {
          setError("That PIN didn't match. We'll ask you to sign in again.");
          setTimeout(() => {
            if (onPinLocked) onPinLocked();
          }, LOCK_TRANSITION_MS);
        } else if (onPinLocked) {
          onPinLocked();
        }
      } else if (err.error === 'PIN_NOT_SET') {
        // PIN was removed from another device/session — unlock
        onUnlock();
      } else if (err.status === 401 || err.status === 403) {
        // v1.1: JWT expired between lock display and PIN entry.
        // Show transitional message, then signal App.jsx to switch to
        // session expired screen. Server 401/403 is the authoritative
        // session validity signal (per OWASP MSTG).
        setError('Your session has expired.');
        setTimeout(() => {
          if (onSessionExpired) onSessionExpired();
        }, LOCK_TRANSITION_MS);
      } else {
        setError("We couldn't check that PIN just now. Please try again in a moment.");
      }
    } finally {
      setVerifying(false);
    }
  }, [pin, verifying, attemptsRemaining, getApi, onUnlock, onSessionExpired, onPinLocked]);

  // v1.2: auto-submit on the final digit, ONLY when the length is known.
  // With pinLength null we are guessing at PIN_MAX_LENGTH, and a shorter PIN
  // would never reach it — auto-submit alone would strand the person.
  useEffect(() => {
    if (!pinLength) return;
    if (verifying) return;
    if (pin.length !== slots) return;
    handleSubmit();
  }, [pin, pinLength, slots, verifying, handleSubmit]);

  // Forgot PIN → Auth0 logout + re-login
  const handleForgotPin = useCallback(() => {
    // Clear ana_ session data (same pattern as SettingsPage handleSignOut)
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('ana_')) keysToRemove.push(key);
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  // Session expired / PIN locked → Auth0 re-login
  const handleSignIn = useCallback(() => {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('ana_')) keysToRemove.push(key);
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  // Keyboard support
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (lockReason === 'session_expired' || lockReason === 'pin_locked') return;

    const handleKeyDown = (e) => {
      if (verifying) return;
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Enter' && pin.length >= PIN_MIN_LENGTH) {
        handleSubmit();
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [lockReason, verifying, pin, handleDigit, handleBackspace, handleSubmit]);

  // Auto-focus for keyboard events
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // ─── PIN Locked Mode (v1.2) ─────────────────────────────────────────

  if (lockReason === 'pin_locked') {
    return (
      <div className={styles.overlay} ref={containerRef} tabIndex={-1}>
        <div className={styles.content}>
          <div className={styles.logo}>
            <img
              src="/butterfly.svg"
              alt=""
              className={styles.butterfly}
              aria-hidden="true"
            />
            <h1 className={styles.wordmark}>ANAMORIA</h1>
          </div>

          <div className={styles.messageSection}>
            <div className={styles.lockIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                stroke="var(--color-text-subtle, #737373)" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </div>
            <h2 className={styles.messageHeading}>Sign in again to continue</h2>
            <p className={styles.messageText}>
              After a few tries with the PIN, we ask you to sign in again. This keeps
              your memories safe if someone else picks up your device. Nothing has
              happened to them, and you can choose a new PIN when you sign in.
            </p>
          </div>

          <button className={styles.btnPrimary} onClick={handleSignIn}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  // ─── Session Expired Mode ───────────────────────────────────────────

  if (lockReason === 'session_expired') {
    return (
      <div className={styles.overlay} ref={containerRef} tabIndex={-1}>
        <div className={styles.content}>
          <div className={styles.logo}>
            <img
              src="/butterfly.svg"
              alt=""
              className={styles.butterfly}
              aria-hidden="true"
            />
            <h1 className={styles.wordmark}>ANAMORIA</h1>
          </div>

          <div className={styles.messageSection}>
            <div className={styles.lockIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                stroke="var(--color-text-subtle, #737373)" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </div>
            <h2 className={styles.messageHeading}>Session expired</h2>
            <p className={styles.messageText}>
              Sign in again, and your memories will be here.
            </p>
          </div>

          <button className={styles.btnPrimary} onClick={handleSignIn}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  // ─── PIN Unlock Mode ────────────────────────────────────────────────

  return (
    <div className={styles.overlay} ref={containerRef} tabIndex={-1}>
      <div className={styles.content}>
        <div className={styles.logo}>
          <img
            src="/butterfly.svg"
            alt=""
            className={styles.butterfly}
            aria-hidden="true"
          />
          <h1 className={styles.wordmark}>ANAMORIA</h1>
        </div>

        <p className={styles.enterPinLabel}>Enter your PIN</p>

        {/* PIN dots — v1.2: one per digit of the member's actual PIN */}
        <div className={styles.dotsRow}>
          {Array.from({ length: slots }, (_, i) => (
            <div
              key={i}
              className={`${styles.dot} ${i < pin.length ? styles.dotFilled : ''}`}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* v1.2: the dots are decorative shapes, so nothing announced the
            count before this. Polite, not assertive — an assertive region
            would interrupt on every keypress, which is worse than silence.
            .visually-hidden is the app's existing utility (global.css). */}
        <div className="visually-hidden" aria-live="polite">
          {pin.length} of {slots} digits entered
        </div>

        {/* Error message */}
        {error && (
          <p className={styles.error} role="alert">{error}</p>
        )}

        {/* Numeric keypad */}
        <div className={styles.keypad}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
            <button
              key={digit}
              className={styles.key}
              onClick={() => handleDigit(String(digit))}
              disabled={verifying || pin.length >= slots}
              type="button"
              aria-label={String(digit)}
            >
              {digit}
            </button>
          ))}

          {/* Bottom row: Forgot PIN / 0 / Backspace */}
          <button
            className={`${styles.key} ${styles.keyAction}`}
            onClick={handleForgotPin}
            type="button"
            aria-label="Forgot PIN"
            disabled={verifying}
          >
            <span className={styles.keyActionLabel}>Forgot?</span>
          </button>
          <button
            className={styles.key}
            onClick={() => handleDigit('0')}
            disabled={verifying || pin.length >= slots}
            type="button"
            aria-label="0"
          >
            0
          </button>
          <button
            className={`${styles.key} ${styles.keyAction}`}
            onClick={handleBackspace}
            disabled={verifying || pin.length === 0}
            type="button"
            aria-label="Delete"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 4H8l-7 8 7 8h13a2 2 0 002-2V6a2 2 0 00-2-2z" />
              <line x1="18" y1="9" x2="12" y2="15" />
              <line x1="12" y1="9" x2="18" y2="15" />
            </svg>
          </button>
        </div>

        {/* Unlock button */}
        <button
          className={styles.btnUnlock}
          onClick={handleSubmit}
          disabled={verifying || pin.length < PIN_MIN_LENGTH}
          type="button"
        >
          {verifying ? 'Checking…' : 'Unlock'}
        </button>
      </div>
    </div>
  );
}
